import {isFeatureEnabled} from 'feature-flags';
import {Card} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  CommunicationActivityLogDatagrid,
  DmsDataGrid,
  openActivityChangeLog,
  Section,
  SectionTab,
} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['commandId'] as const;

export function ActivityLog() {
  const {id} = useRequiredParams();

  const gridCode = isFeatureEnabled(featureFlags.SALE_INTEREST_ACTION_LOG)
    ? 'interest-audit-log'
    : 'interest-action-log';

  const emailActivityLogQueryModifier = useCallback(
    (filter: QueryFilterObject) => ({...filter, recordId: id, customerId: undefined}),
    [id]
  );

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {record: id}]),
    [id]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const {actionKey, getColumnData} = data;
    const commandId = getColumnData('commandId')?.value;

    match(actionKey).with('detail', () => {
      if (commandId && typeof commandId === 'string') {
        openActivityChangeLog({resourceId: EntityResourceIds.interest, commandId, recordId: id});
      } else {
        console.error('no command in row');
      }
    });
  };

  const tabs: SectionTab[] = [
    {
      content: (
        <Card title={i18n.t('general.labels.activityLogs')} key="activity-logs-datagrid">
          <Box>
            <DmsDataGrid
              actionCallback={actionCallback}
              predefinedColumns={PREDEFINED_COLUMNS}
              gridCode={gridCode}
              autoHeight
              queryModifier={queryModifier}
              data-testid="interest-activity-logs-datagrid"
            />
          </Box>
        </Card>
      ),
      title: i18n.t('general.labels.activityLogs'),
      queryId: 'interest-action-log',
    },
    {
      content: (
        <Card title={i18n.t('general.labels.emailActivityLogs')} key="email-activity-logs-datagrid">
          <Box height={130}>
            <CommunicationActivityLogDatagrid
              queryModifier={emailActivityLogQueryModifier}
              presetInstanceId="interest-email-activity-logs"
              data-testid="interest-email-activity-logs-datagrid"
            />
          </Box>
        </Card>
      ),
      title: i18n.t('general.labels.emailActivityLogs'),
      queryId: 'interest-email-activity-log',
    },
  ];

  return <Section tabs={tabs} />;
}
